import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface CancellationRecord {
  id: string;
  restaurant_id: string;
  order_id: string;
  order_item_id: string;
  product_id: string | null;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes: string | null;
  reason: string | null;
  cancelled_by: string;
  created_by: string | null;
  cancelled_at: string;
  created_at: string;
  cancelled_by_profile?: {
    full_name: string | null;
  };
  created_by_profile?: {
    full_name: string | null;
  };
  order?: {
    order_number: number;
    table_number: string | null;
    tab_number: string | null;
  };
}

export function useCancellationsReport() {
  const { activeRestaurant } = useRestaurantContext();
  const [cancellations, setCancellations] = useState<CancellationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCancellations, setTotalCancellations] = useState(0);
  const [totalValue, setTotalValue] = useState(0);

  const fetchCancellations = useCallback(
    async (options?: {
      dateFrom?: Date;
      dateTo?: Date;
      periodId?: string;
      page?: number;
      pageSize?: number;
    }) => {
      if (!activeRestaurant) {
        setIsLoading(false);
        return;
      }

      const page = options?.page || 1;
      const pageSize = options?.pageSize || 50;
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      setIsLoading(true);
      try {
        let query = supabase
          .from("order_item_cancellations")
          .select("*", { count: "exact" })
          .eq("restaurant_id", activeRestaurant.id)
          .order("cancelled_at", { ascending: false })
          .range(from, to);

        // Se periodId fornecido, buscar datas do período operacional
        if (options?.periodId) {
          const { data: period } = await supabase
            .from("operational_periods")
            .select("opened_at, closed_at")
            .eq("id", options.periodId)
            .single();
          
          if (period) {
            query = query.gte("cancelled_at", period.opened_at);
            if (period.closed_at) {
              query = query.lte("cancelled_at", period.closed_at);
            }
          }
        } else {
          // Filtro por data - criar timestamps corretos para timezone local
          if (options?.dateFrom) {
            const startDate = new Date(options.dateFrom);
            startDate.setHours(0, 0, 0, 0);
            query = query.gte("cancelled_at", startDate.toISOString());
          }
          if (options?.dateTo) {
            const endDate = new Date(options.dateTo);
            endDate.setHours(23, 59, 59, 999);
            query = query.lte("cancelled_at", endDate.toISOString());
          }
        }

        const { data, error, count } = await query;

        if (error) throw error;

        // Fetch profiles for cancelled_by and created_by
        const cancelledByIds = [...new Set((data || []).map((c: any) => c.cancelled_by).filter(Boolean))];
        const createdByIds = [...new Set((data || []).map((c: any) => c.created_by).filter(Boolean))];
        const allUserIds = [...new Set([...cancelledByIds, ...createdByIds])];

        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", allUserIds);

        const profileMap = new Map(
          (profiles || []).map((p: any) => [p.id, p])
        );

        // Fetch order info
        const orderIds = [...new Set((data || []).map((c: any) => c.order_id))];
        const { data: orders } = await supabase
          .from("orders")
          .select("id, order_number, table_number, tab_number")
          .in("id", orderIds);

        const orderMap = new Map(
          (orders || []).map((o: any) => [o.id, o])
        );

        const cancellationsWithProfiles = (data || []).map((c: any) => ({
          ...c,
          cancelled_by_profile: profileMap.get(c.cancelled_by) || { full_name: null },
          created_by_profile: c.created_by ? profileMap.get(c.created_by) || { full_name: null } : null,
          order: orderMap.get(c.order_id) || null,
        }));

        setCancellations(cancellationsWithProfiles as CancellationRecord[]);
        setTotalCancellations(count || 0);

        // Calculate total value
        const total = (data || []).reduce((sum: number, c: any) => sum + Number(c.total_price), 0);
        setTotalValue(total);
      } catch (error) {
        console.error("Error fetching cancellations:", error);
        toast.error("Erro ao carregar cancelamentos");
      } finally {
        setIsLoading(false);
      }
    },
    [activeRestaurant]
  );

  return {
    cancellations,
    totalCancellations,
    totalValue,
    isLoading,
    fetchCancellations,
  };
}
